import { Dispatch, MutableRefObject, SetStateAction, useCallback } from 'react';
import ActionsMenu from '../../../../components/ActionsMenu/ActionsMenu.component';
import { Examiner, ITaskGroup } from '../../../../models/taskGroup.types';
import { ActionMenuItem } from '../../../../components/ActionsMenu/ActionsMenu.types';
import { MessageModal } from '../../ExaminerTreemap.types';
import { SubText } from '../../../../components/Prompt/Prompt.component';
import { getIsExaminerBlocked } from '../../Helpers';

export type UseVerifierActionsMenuProps = {
  data: ITaskGroup[];
  selectedItemId: MutableRefObject<string>;
  selectedTaskGroup: MutableRefObject<ITaskGroup | undefined>;
  setAction: Dispatch<SetStateAction<string>>;
  setOpenEnableExaminerPrompt: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDisableExaminerPrompt: React.Dispatch<React.SetStateAction<boolean>>;
  setMessageModal: React.Dispatch<React.SetStateAction<MessageModal>>;
  setEditExaminerNumberModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPromptMainText: React.Dispatch<React.SetStateAction<string>>;
  setPromptSubTexts: React.Dispatch<React.SetStateAction<SubText[] | undefined>>;
  setIsConfirmationPromptVisible: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useVerifierActionsMenu = ({
  data,
  selectedItemId,
  selectedTaskGroup,
  setAction,
  setOpenEnableExaminerPrompt,
  setOpenDisableExaminerPrompt,
  setMessageModal,
  setEditExaminerNumberModalOpen,
  setPromptMainText,
  setPromptSubTexts,
  setIsConfirmationPromptVisible,
}: UseVerifierActionsMenuProps) => {
  const displayRemoveVerifierFunctionPrompt = useCallback(() => {
    const taskGroup = data.find((tg) =>
      tg.examTaskGroupCoordinators.some((coordinator) =>
        coordinator.examiningTeamChairmen.some((chairman) =>
          chairman.examiningTeam?.examiningTeamMembers?.some(
            (member) => member.examiner.examinerId === selectedItemId.current,
          ),
        ),
      ),
    );

    if (taskGroup) {
      selectedTaskGroup.current = taskGroup;
      setPromptMainText('Czy na pewno chcesz usunąć Egzaminatorowi:');

      const verifier = taskGroup.examTaskGroupCoordinators
        .flatMap((coordinator) => coordinator.examiningTeamChairmen)
        .flatMap((chairman) => chairman.examiningTeam?.examiningTeamMembers ?? [])
        .find((member) => member.examiner.examinerId === selectedItemId.current)?.examiner;

      const subTexts: SubText[] = [
        { text: `${verifier?.firstName} ${verifier?.lastName}` },
        { text: 'przypisaną funkcję Egzaminator-Weryfikator?', isBold: true },
      ];
      setPromptSubTexts(subTexts);
      setIsConfirmationPromptVisible(true);
    }
  }, [data]);

  const getVerifierActionsMenu = useCallback(
    (examiner: Examiner, assessmentId: string, rowIndex: number) => {
      const menuItems: ActionMenuItem[] = [];

      menuItems.push({
        text: 'Usuń egzaminatorowi funkcję Egzaminator-Weryfikator',
        onClick: () => {
          setAction('Usuń egzaminatorowi funkcję Egzaminator-Weryfikator');
          displayRemoveVerifierFunctionPrompt();
        },
      });
      if (getIsExaminerBlocked(examiner, assessmentId)) {
        menuItems.push({
          text: 'Odblokuj Egzaminatora-Weryfikatora',
          onClick: () => {
            setAction('Odblokuj Egzaminatora-Weryfikatora');
            setOpenEnableExaminerPrompt(true);
          },
        });
      } else {
        menuItems.push({
          text: 'Zablokuj Egzaminatora-Weryfikatora',
          onClick: () => {
            setAction('Zablokuj Egzaminatora-Weryfikatora');
            setOpenDisableExaminerPrompt(true);
          },
        });
      }
      menuItems.push({
        text: 'Wyślij wiadomość',
        onClick: () => {
          setAction('Wyślij wiadomość');
          setMessageModal({ open: true, type: 'standard' });
        },
      });
      menuItems.push({
        text: 'Edytuj numer Egzaminatora-Weryfikatora',
        onClick: () => {
          setAction('Edytuj numer Egzaminatora-Weryfikatora');
          setEditExaminerNumberModalOpen(true);
        },
      });

      return <ActionsMenu menuItems={menuItems} rowIndex={rowIndex} />;
    },
    [
      setAction,
      displayRemoveVerifierFunctionPrompt,
      setOpenEnableExaminerPrompt,
      setOpenDisableExaminerPrompt,
      setMessageModal,
      setEditExaminerNumberModalOpen,
    ],
  );

  return {
    getVerifierActionsMenu,
  };
};
